import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef } from 'react';
import { Chart } from 'chart.js';
import { DEFAULT_THEME, PRODUCT_NAME, barOrientation, formatNumber, showDots, showEndLabels, showValueLabels } from '@tallyframe/core';
import { AXIS_LABEL_FIT_PLUGIN_ID, axisLabelFitPlugin, axisTickDecimals, CHART_FONT_FAMILY, DEFAULT_CHART_COLORS, ensureChartsRegistered, resolveChartColors } from './chart-setup.js';
import { endLabelPlugin, valueLabelPlugin } from './chart-labels.js';
import { BrandContext } from './brand.js';
import { fontLoadShorthands } from './theme-style.js';
/** The bitmap scale for a fixed-size (print) chart. The PDF's headless
 *  Chromium reports a device pixel ratio of 1, so Chart.js's default would put
 *  a 1x bitmap in the PDF, which blurs as soon as it is zoomed or printed. 3x
 *  is close to 300 dpi at 96 CSS px per inch. Screen charts keep the device's
 *  own ratio. */
export const PRINT_PIXEL_RATIO = 3;
/** A canvas can't read CSS custom properties, so the report font is read
 *  from the canvas's own computed style (spec §7.1) — inherited down from
 *  `tokens.css`'s `--rp-font` on whichever `.tf-root`/`.tf-surface`/`.tf-print`
 *  ancestor set it. Falls back to the literal default when nothing did (no
 *  stylesheet loaded at all, as in a unit test). */
function resolveFontFamily(canvas) {
    if (typeof getComputedStyle !== 'function')
        return CHART_FONT_FAMILY;
    const value = getComputedStyle(canvas).getPropertyValue('--rp-font').trim();
    return value || CHART_FONT_FAMILY;
}
/**
 * Builds a Chart.js configuration from a view and the resolved brand (spec
 * §7.1–7.4). Pure and exported so tests can inspect the shape directly
 * instead of reaching into a live Chart.js instance.
 */
export function buildConfig(view, opts) {
    const colors = opts.colors ?? DEFAULT_CHART_COLORS;
    const locale = opts.locale ?? 'en-US';
    const { widget } = view;
    const stacked = widget.stacked;
    const isLine = widget.chartType === 'line';
    // Only a bar chart ever flips to horizontal — a line chart's category axis
    // always runs left-to-right as a timeline or sequence.
    const horizontal = widget.chartType === 'bar' && barOrientation(view) === 'horizontal';
    const dots = showDots(view);
    const palette = opts.brand.palette;
    const fontFamily = opts.fontFamily;
    const datasets = view.series.map((series, index) => {
        const color = palette[index % palette.length] ?? palette[0] ?? colors.neutral;
        const shared = {
            label: series.label,
            // `values` carries null where the metric was unavailable. spanGaps:false
            // makes Chart.js leave a hole rather than join across it, and a null is
            // never drawn as a zero.
            data: series.values,
            spanGaps: false,
            ...(stacked ? { stack: 'tf-stack' } : {})
        };
        if (isLine) {
            return {
                ...shared,
                borderColor: color,
                backgroundColor: color,
                pointBackgroundColor: color,
                borderWidth: 2,
                // The neutral pool color reads as "series 2, no strong identity" —
                // dashing it (and only it) is the one non-color cue every reader
                // gets, colorblind or not.
                borderDash: color === DEFAULT_CHART_COLORS.neutral ? [4, 3] : [],
                fill: false,
                pointRadius: dots ? 2.5 : 0,
                tension: 0.15
            };
        }
        // Grouped (multi-series, unstacked) bars get a 2px gap (spec §7.2).
        // Chart.js has no fixed-pixel gap option, so the mechanism is:
        // `barPercentage: 1` makes the bars within a category touch, and a
        // 1px paper-colored border on the sides that face a neighboring
        // series' bar — top/bottom for horizontal bars, left/right for
        // vertical ones — insets each one, leaving 2px of paper between two
        // adjacent bars. `borderSkipped` is left at Chart.js's default
        // ('start', the baseline edge), so the baseline stays flush at zero
        // and the bar's value end (unaffected by this border) stays square.
        // Stacked bars, and a lone series with nothing to gap against, keep
        // the plain border they always had.
        const grouped = !stacked && view.series.length >= 2;
        return {
            ...shared,
            backgroundColor: color,
            borderColor: grouped ? colors.paper : color,
            borderWidth: grouped ? (horizontal ? { top: 1, bottom: 1 } : { left: 1, right: 1 }) : 0,
            borderRadius: 0,
            fill: false,
            ...(grouped ? { barPercentage: 1 } : {})
        };
    });
    // Axis labels are muted — never full ink — so they read as scaffolding
    // around the data rather than competing with it (spec §7.4).
    //
    // Both axes also refuse to rotate. Turning text on its side is the reflex
    // Chart.js reaches for when labels stop fitting (its default allowance is 50
    // degrees), and the design review named a tilted label as a fail condition.
    // The value axis needs saying as much as the category one: it was inheriting
    // that 50-degree allowance silently. Pinned here, per chart, rather than on
    // `Chart.defaults` — this is ReportPad's ruling about ReportPad's charts, and
    // a host embedding the renderer keeps rotation on its own (see
    // `ensureChartsRegistered`).
    const noRotation = { maxRotation: 0, minRotation: 0 };
    // Chart.js's autoSkip thins a crowded category axis before our label-fit
    // plugin runs, so names never get the chance to be shortened to their slot.
    // Categories other than dates opt out and let `axisLabelFitPlugin` shorten
    // first and thin only what still collides; date axes keep autoSkip.
    const shortenCategories = view.dimensionType !== 'date';
    const categoryTicks = {
        ...noRotation,
        autoSkip: !shortenCategories,
        color: colors.muted,
        font: { family: fontFamily, size: 11 }
    };
    // Value ticks speak the widget's number format ("4%", "$20,000"), with
    // decimals set by the tick step rather than the widget's display decimals.
    const format = widget.format;
    const valueTicks = {
        ...noRotation,
        color: colors.muted,
        font: { family: fontFamily, size: 11 },
        callback: (value, _index, ticks) => {
            const n = Number(value);
            if (!format || !Number.isFinite(n))
                return String(value);
            const step = ticks.length > 1 ? Math.abs(ticks[1].value - ticks[0].value) : Math.abs(n);
            return formatNumber(n, { ...format, decimals: axisTickDecimals(step, format.style) }, locale);
        }
    };
    const categoryScale = { stacked, grid: { display: false }, ticks: categoryTicks };
    const valueScale = {
        stacked,
        beginAtZero: true,
        grid: { color: colors.grid },
        border: { display: false },
        ticks: valueTicks
    };
    // In-house plugins draw values directly on the chart (spec §7.2-7.3),
    // rather than relying on Chart.js's built-in tooltip/legend to carry them.
    // Each is only ever included when its own layout rule says the labels
    // will stay legible.
    //
    // `axisLabelFitPlugin` leads the array so it keeps the position it held when
    // it was a registered plugin: Chart.js runs registered plugins before a
    // chart's own, and this one is the only reader of the others' layout.
    const plugins = [axisLabelFitPlugin];
    if (showValueLabels(view))
        plugins.push(valueLabelPlugin(view, fontFamily, colors));
    if (showEndLabels(view))
        plugins.push(endLabelPlugin(view, fontFamily, colors));
    return {
        type: widget.chartType,
        data: { labels: view.categories, datasets },
        plugins,
        options: {
            indexAxis: horizontal ? 'y' : 'x',
            responsive: opts.responsive,
            ...(opts.pixelRatio === undefined ? {} : { devicePixelRatio: opts.pixelRatio }),
            maintainAspectRatio: false,
            animation: opts.animate ? undefined : false,
            normalized: true,
            interaction: { mode: 'index', intersect: false },
            layout: { padding: 0 },
            plugins: {
                // Shorten category names to their slot before thinning drops any;
                // never for a date axis, where "2026-0…" would be a wrong date.
                [AXIS_LABEL_FIT_PLUGIN_ID]: { shortenCategories },
                legend: {
                    // Hidden for one series, whose title already names it.
                    display: view.series.length >= 2,
                    position: 'top',
                    align: 'start',
                    labels: { boxWidth: 10, boxHeight: 10, color: colors.muted, font: { family: fontFamily, size: 11 } }
                },
                tooltip: {
                    backgroundColor: colors.ink,
                    titleColor: colors.paper,
                    bodyColor: colors.paper,
                    titleFont: { family: fontFamily },
                    bodyFont: { family: fontFamily },
                    callbacks: {
                        // Reuse core's preformatted strings so a tooltip can never disagree
                        // with the KPI, the data table or the CSV export.
                        label: (item) => {
                            const series = view.series[item.datasetIndex];
                            const display = series?.displays[item.dataIndex];
                            return `${series?.label ?? ''}: ${display ?? ''}`;
                        }
                    }
                }
            },
            scales: horizontal ? { y: categoryScale, x: valueScale } : { x: categoryScale, y: valueScale }
        }
    };
}
/**
 * A Chart.js instance with a React lifetime.
 *
 * The instance is created in an effect and destroyed in its cleanup, so it is
 * torn down on unmount *and* whenever the view or sizing changes. Nothing leaks
 * a detached canvas.
 */
export function ChartCanvas(props) {
    const { view, animate, width, height } = props;
    const canvasRef = useRef(null);
    const onRenderedRef = useRef(props.onRendered);
    onRenderedRef.current = props.onRendered;
    const brand = useContext(BrandContext);
    const theme = brand.theme ?? DEFAULT_THEME;
    const fixed = width !== undefined && height !== undefined;
    const drawable = view.unavailableReason === undefined;
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !drawable) {
            // Nothing to draw — still signal once, exactly like the drawing path
            // below, so a caller counting renders (PrintReport) is never stuck
            // waiting on a chart that will never exist. Deferred through a
            // microtask and guarded by `cancelled`, same as the drawable path
            // below: under StrictMode's mount→cleanup→mount, an unguarded
            // synchronous call here fires once per mount (twice total) — the
            // cleanup from the first, discarded mount must be able to stop its
            // own callback from ever running.
            let cancelled = false;
            void Promise.resolve().then(() => {
                if (!cancelled)
                    onRenderedRef.current?.();
            });
            return () => {
                cancelled = true;
            };
        }
        let cancelled = false;
        let chart;
        ensureChartsRegistered();
        // Chart.js measures text as soon as it draws, so a chart built before the
        // report font has actually loaded gets laid out against the fallback
        // font's metrics. Waiting for `document.fonts.ready` (falling back to an
        // already-resolved promise where the platform has no FontFaceSet at all)
        // means every chart — screen and PDF alike — draws once, correctly.
        const draw = () => {
            if (cancelled)
                return;
            try {
                chart = new Chart(canvas, buildConfig(view, {
                    animate,
                    responsive: !fixed,
                    brand,
                    fontFamily: resolveFontFamily(canvas),
                    colors: resolveChartColors(canvas),
                    locale: theme.locale,
                    pixelRatio: fixed ? PRINT_PIXEL_RATIO : undefined
                }));
            }
            catch (error) {
                // A chart that cannot draw must not take the page down with it, and it
                // must not stall an export either — the accessible data table below
                // still carries every number.
                // eslint-disable-next-line no-console
                console.error(`${PRODUCT_NAME}: chart failed to render`, error);
            }
            onRenderedRef.current?.();
        };
        // The theme's faces are requested with a CSS font shorthand first: a face
        // nothing on the page has used yet is never fetched by `fonts.ready` alone.
        const fonts = document.fonts;
        const loads = fonts && typeof fonts.load === 'function'
            ? Promise.all(fontLoadShorthands(theme).map((f) => fonts.load(f))).then(() => fonts.ready)
            : (fonts?.ready ?? Promise.resolve());
        // A font that never loads must not wedge an export forever — draw either way.
        void loads.then(draw, draw);
        // A canvas paints its colors once, so a theme switch on the document
        // (`data-theme` or a class on <html>) rebuilds the chart in the new colors.
        const observer = typeof MutationObserver === 'function'
            ? new MutationObserver(() => {
                if (cancelled || !chart)
                    return;
                chart.destroy();
                chart = undefined;
                draw();
            })
            : undefined;
        observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
        return () => {
            cancelled = true;
            observer?.disconnect();
            chart?.destroy();
        };
    }, [view, animate, fixed, drawable, width, height, brand, theme]);
    if (!drawable) {
        return (_jsx("p", { className: "tf-widget-unavailable", role: "status", children: view.unavailableReason }));
    }
    return (_jsx("div", { className: fixed ? 'tf-chart-canvas tf-chart-canvas--fixed' : 'tf-chart-canvas', style: fixed ? { width: `${width}px`, height: `${height}px` } : undefined, children: _jsx("canvas", { ref: canvasRef, width: fixed ? width : undefined, height: fixed ? height : undefined, role: "presentation", "aria-hidden": "true" }) }));
}
//# sourceMappingURL=ChartCanvas.js.map